import React, { createContext, useContext, useState, useCallback } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { Menu, X, Search, Package, Camera, BarChart3, LogOut, AlertTriangle, Settings, RotateCcw, Zap, Scan, AlertOctagon, ClipboardCheck, DollarSign, Tag, Store, Boxes } from 'lucide-react'
import { useTheme } from '../contexts/ThemeContext'
import { useAuth } from '../contexts/AuthContext'
import { useMobile } from '../contexts/MobileContext'
import { useNotification } from '../contexts/NotificationContext'
import { Button } from './ui/Button'
import { Badge } from './ui/Badge'
import { ThemeSelector } from './ui/ThemeSelector'
import { EbayTopBarStatus } from './ebay/EbayTopBarStatus'

export interface PageConfig {
  id: string
  title: string
  description?: string
  actions?: Array<{
    id: string
    label: string
    variant?: 'primary' | 'secondary' | 'destructive'
    onClick: () => void
  }>
  layout?: {
    containerWidth?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | 'full'
    columns?: number
  }
  breadcrumbs?: Array<{
    label: string
    href?: string
  }>
}

interface ShellContextType {
  openPanel: (panel: React.ReactNode) => void
  closePanel: () => void
  isPanelOpen: boolean
  showConfirm: (message: string, onConfirm: () => void, onCancel?: () => void) => void
  hideConfirm: () => void
  confirmDialog: {
    message: string
    onConfirm: () => void
    onCancel?: () => void
  } | null
  toast: (toast: { title: string; description?: string; type: 'success' | 'error' | 'warning' | 'info' }) => void
}

const ShellContext = createContext<ShellContextType | undefined>(undefined)

export function useShell() {
  const context = useContext(ShellContext)
  if (context === undefined) {
    throw new Error('useShell must be used within a PageShell')
  }
  return context
}

interface PageShellProps {
  config: PageConfig
  children: React.ReactNode
}

export function PageShell({ config, children }: PageShellProps) {
  const { setTheme, resolvedTheme } = useTheme()
  const { user, logout } = useAuth()
  const { isMobile } = useMobile()
  const { success, error, warning, info } = useNotification()
  const location = useLocation()

  // Defensive coding: ensure config is defined with sensible defaults
  const safeConfig: PageConfig = config ?? {
    id: 'unknown',
    title: 'Loading...',
    description: '',
  }
  
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [panel, setPanel] = useState<React.ReactNode>(null)
  const [confirmDialog, setConfirmDialog] = useState<{
    message: string
    onConfirm: () => void
    onCancel?: () => void
  } | null>(null)

  const openPanel = useCallback((panel: React.ReactNode) => {
    setPanel(panel)
  }, [])

  const closePanel = useCallback(() => {
    setPanel(null)
  }, [])

  const showConfirm = useCallback((message: string, onConfirm: () => void, onCancel?: () => void) => {
    setConfirmDialog({ message, onConfirm, onCancel })
  }, [])

  const hideConfirm = useCallback(() => {
    setConfirmDialog(null)
  }, [])

  const toast = useCallback((toastData: { title: string; description?: string; type: 'success' | 'error' | 'warning' | 'info' }) => {
    if (toastData.type === 'success') success(toastData.title, toastData.description)
    else if (toastData.type === 'error') error(toastData.title, toastData.description)
    else if (toastData.type === 'warning') warning(toastData.title, toastData.description)
    else if (toastData.type === 'info') info(toastData.title, toastData.description)
  }, [success, error, warning, info])

  const shellContext: ShellContextType = {
    openPanel,
    closePanel,
    isPanelOpen: !!panel,
    showConfirm,
    hideConfirm,
    confirmDialog,
    toast,
  }

  const navigation = [
    { name: 'Home', href: '/', icon: BarChart3 },
    { name: 'Quick Intake', href: '/quick-intake', icon: Scan },
    // { name: 'Intake', href: '/intake', icon: Package }, // Hidden - replaced by Quick Intake
    { name: 'Search', href: '/search', icon: Search },
    { name: 'FM', href: '/flea-market', icon: Store },
    { name: 'Review', href: '/review', icon: ClipboardCheck },
    { name: 'Pallet Processing', href: '/pallet-processing', icon: Boxes },
    { name: 'Sales', href: '/admin/nifty-inventory', icon: DollarSign },
    { name: 'Etsy', href: '/admin/etsy-candidates', icon: Tag },
    { name: 'FUBAR', href: '/fubar', icon: AlertOctagon },
    { name: 'Scanner', href: '/qr-scanner', icon: Camera },
    // { name: 'Returns', href: '/returns', icon: RotateCcw }, // Deactivated - returns functionality disabled
    { name: 'Totes', href: '/admin/totes', icon: Package },
    { name: 'Admin', href: '/admin', icon: Settings },
    ...(user?.has_escaperoute_access ? [{ name: 'EscapeRoute', href: '/escaperoute', icon: Zap }] : []),
  ]

  const containerWidth = safeConfig.layout?.containerWidth || 'xl'
  const containerClasses: Record<string, string> = {
    sm: 'max-w-2xl',
    md: 'max-w-4xl',
    lg: 'max-w-6xl',
    xl: 'max-w-7xl',
    '2xl': 'max-w-8xl',
    full: 'max-w-full',
  }

  return (
    <ShellContext.Provider value={shellContext}>
      <div
        className="min-h-screen"
        style={{
          background: 'var(--theme-bg)',
          backgroundImage: 'var(--theme-bg-image, none)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed',
          fontFamily: 'Rajdhani, sans-serif'
        }}
      >
        {/* Header */}
        <header style={{
          position: 'sticky',
          top: 0,
          zIndex: 50,
          width: '100%',
          borderBottom: '2px solid var(--theme-glass-border-strong)',
          background: 'var(--theme-glass-bg-heavy)',
          backdropFilter: 'var(--theme-glass-blur)',
          boxShadow: 'var(--theme-glow-soft)'
        }}>
          <div className="container mx-auto px-4 py-4">
            <div className="flex items-center justify-between flex-wrap gap-y-2">
              <div className="flex items-center space-x-4">
                {safeConfig.breadcrumbs && safeConfig.breadcrumbs.length > 0 && (
                  <nav className="flex items-center space-x-2 text-sm" style={{ color: 'var(--theme-text-tertiary)' }}>
                    {safeConfig.breadcrumbs.map((crumb, index) => (
                      <React.Fragment key={index}>
                        {index > 0 && <span>/</span>}
                        {crumb.href ? (
                          <Link to={crumb.href} style={{ color: 'var(--theme-primary)', textDecoration: 'none' }}>
                            {crumb.label}
                          </Link>
                        ) : (
                          <span>{crumb.label}</span>
                        )}
                      </React.Fragment>
                    ))}
                  </nav>
                )}
              </div>

              <div className="flex items-center space-x-2 flex-wrap gap-y-2 justify-end">
                {/* Desktop Navigation */}
                {!isMobile && (
                  <nav className="flex items-center space-x-2 flex-wrap gap-y-2 justify-end">
                    {navigation.map((item) => {
                      const isActive = location.pathname === item.href
                      return (
                        <Link
                          key={item.name}
                          to={item.href}
                          className="nav-link"
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            padding: '10px 20px',
                            borderRadius: 'var(--theme-radius-md)',
                            transition: 'all 0.3s',
                            backgroundColor: isActive ? 'var(--theme-glass-bg)' : 'transparent',
                            color: isActive ? 'var(--theme-primary)' : 'var(--theme-text-primary)',
                            border: isActive ? '2px solid var(--theme-primary)' : '2px solid var(--theme-glass-border)',
                            textDecoration: 'none',
                            fontSize: '14px',
                            fontWeight: 700,
                            textTransform: 'uppercase',
                            letterSpacing: '1px',
                            boxShadow: isActive ? 'var(--theme-glow-medium)' : 'none'
                          }}
                          onMouseEnter={(e) => {
                            if (!isActive) {
                              e.currentTarget.style.borderColor = 'var(--theme-glass-border-strong)'
                              e.currentTarget.style.boxShadow = 'var(--theme-glow-soft)'
                              e.currentTarget.style.color = 'var(--theme-primary)'
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (!isActive) {
                              e.currentTarget.style.borderColor = 'var(--theme-glass-border)'
                              e.currentTarget.style.boxShadow = 'none'
                              e.currentTarget.style.color = 'var(--theme-text-primary)'
                            }
                          }}
                        >
                          <item.icon className="h-4 w-4" />
                          <span>{item.name}</span>
                        </Link>
                      )
                    })}
                  </nav>
                )}

                {/* Theme Selector */}
                <ThemeSelector compact />

                {/* eBay Connection Status */}
                {!isMobile && <EbayTopBarStatus />}

                {/* User Menu */}
                {user && (
                  <div className="flex items-center space-x-2">
                    <div style={{
                      padding: '6px 12px',
                      borderRadius: 'var(--theme-radius-sm)',
                      background: 'var(--theme-glass-bg)',
                      border: '1px solid var(--theme-glass-border)',
                      color: 'var(--theme-primary)',
                      fontSize: '12px',
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}>
                      {user.name}
                    </div>
                    <button
                      onClick={logout}
                      style={{
                        padding: '8px',
                        borderRadius: 'var(--theme-radius-sm)',
                        background: 'transparent',
                        border: '1px solid var(--theme-glass-border)',
                        color: 'var(--theme-text-primary)',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = 'var(--theme-error-bg, rgba(239, 68, 68, 0.2))'
                        e.currentTarget.style.borderColor = 'var(--theme-error)'
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent'
                        e.currentTarget.style.borderColor = 'var(--theme-glass-border)'
                      }}
                    >
                      <LogOut className="h-4 w-4" />
                    </button>
                  </div>
                )}

                {/* Mobile Menu Button */}
                {isMobile && (
                  <button
                    onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                    style={{
                      padding: '8px',
                      borderRadius: 'var(--theme-radius-sm)',
                      background: 'transparent',
                      border: '1px solid var(--theme-glass-border)',
                      color: 'var(--theme-text-primary)',
                      cursor: 'pointer',
                      transition: 'all 0.2s'
                    }}
                  >
                    {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                  </button>
                )}
              </div>
            </div>

            {/* Mobile Navigation */}
            {isMobile && mobileMenuOpen && (
              <div className="mt-4 pb-4" style={{ borderTop: '1px solid var(--theme-glass-border)' }}>
                <div className="flex flex-col space-y-2 pt-4">
                  {navigation.map((item) => {
                    const isActive = location.pathname === item.href
                    return (
                      <Link
                        key={item.name}
                        to={item.href}
                        onClick={() => setMobileMenuOpen(false)}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '12px',
                          padding: '12px 16px',
                          borderRadius: 'var(--theme-radius-sm)',
                          transition: 'all 0.2s',
                          backgroundColor: isActive ? 'var(--theme-primary)' : 'transparent',
                          color: isActive ? 'var(--theme-button-text)' : 'var(--theme-text-primary)',
                          border: isActive ? 'none' : '1px solid var(--theme-glass-border)',
                          textDecoration: 'none',
                          fontSize: '14px',
                          fontWeight: 600,
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                          boxShadow: isActive ? 'var(--theme-glow-medium)' : 'none'
                        }}
                      >
                        <item.icon className="h-5 w-5" />
                        <span>{item.name}</span>
                      </Link>
                    )
                  })}

                  {/* Theme Selector in Mobile Menu */}
                  <div className="pt-4 mt-4" style={{ borderTop: '1px solid var(--theme-glass-border)' }}>
                    <div style={{
                      fontSize: '10px',
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      color: 'var(--theme-text-tertiary)',
                      marginBottom: '8px',
                      paddingLeft: '16px'
                    }}>
                      Theme
                    </div>
                    <div style={{ paddingLeft: '12px' }}>
                      <ThemeSelector compact={false} />
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </header>

        {/* Main Content */}
        <main className="flex-1">
          <div className={`container mx-auto px-4 py-8 ${containerClasses[containerWidth]}`}>
            {children}
          </div>
        </main>

        {/* Actions */}
        {safeConfig.actions && safeConfig.actions.length > 0 && (
          <div className="sticky bottom-0 z-40 w-full border-t border-outline bg-surface/95 backdrop-blur supports-[backdrop-filter]:bg-surface/60">
            <div className="container mx-auto px-4 py-4">
              <div className="flex justify-end space-x-2">
                {safeConfig.actions.map((action) => (
                  <Button
                    key={action.id}
                    variant={action.variant || 'primary'}
                    onClick={action.onClick}
                  >
                    {action.label}
                  </Button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Panel Overlay */}
        {panel && (
          <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm">
            <div className="absolute right-0 top-0 h-full w-96 bg-surface border-l border-outline shadow-xl">
              <div className="p-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-content">Panel</h3>
                  <Button variant="ghost" size="sm" onClick={closePanel}>
                    <X className="h-4 w-4" />
                  </Button>
                </div>
                {panel}
              </div>
            </div>
          </div>
        )}

        {/* Confirm Dialog */}
        {confirmDialog && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
            <div className="bg-surface p-6 rounded-lg shadow-lg max-w-md w-full border border-outline">
              <div className="text-center mb-4">
                <AlertTriangle className="h-12 w-12 text-warning mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-content">Confirm Action</h3>
              </div>
              <p className="text-content-secondary mb-6 text-center">{confirmDialog.message}</p>
              <div className="flex justify-center gap-4">
                <Button variant="secondary" onClick={confirmDialog.onCancel || hideConfirm}>
                  Cancel
                </Button>
                <Button variant="destructive" onClick={() => { confirmDialog.onConfirm(); hideConfirm(); }}>
                  Confirm
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </ShellContext.Provider>
  )
}
